import React, { useState, useContext, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Tab, TabsNavigation } from 'pepsico-ds';
import { useSelector } from 'react-redux';
import { TabContext } from './TabContext';
import type { CmsNode, CmsWrapperProps } from './types';
import CmsRenderer from '../CmsRenderer';
import { FilterContext } from 'components/FilterBar/FilterProvider';
import { useTabContent } from 'hooks/useTabContent';
import { getDefaultTabSlug, findTabIndexBySlug } from 'constants/CmsConstants';
import { selectCmsCurrentProductId } from 'store/slices/cmsSlice';
import { selectFilterValue } from 'store/slices/filterSliceV2';
import Loader from 'components/Loader/Loader';
import { constants } from 'constants/Constants';
import { helperFunctions } from 'utils/helperFunctions';

const StyledProductTabs = styled.div`
  width: 100%;
`;

const FullWidthTabsContainer = styled.div`
  width: 100%;
  border-bottom: 1px solid var(--color-border-default);

  /* Remove the individual tab bottom borders since we have a full-width one */
  .Stab.line.horizontal {
    border-bottom: none;
  }

  /* Keep the active tab indicator */
  .Stab.line.horizontal[aria-selected='true'],
  .Stab.line.horizontal.Stab-selected {
    border-bottom: 2px solid var(--color-border-focus);
    margin-bottom: -1px;
  }
`;

const StyledTabContent = styled.div`
  margin-top: 16px;
  width: 100%;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
`;

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: var(--color-text-error, #d32f2f);
  font-size: 14px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: var(--color-text-secondary, #666);
  font-size: 14px;
`;

/**
 * ProductWrapper - Provides tab selection state context
 */
export const ProductWrapper: React.FC<CmsWrapperProps> = ({ children }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <TabContext.Provider value={{ selectedIndex, setSelectedIndex }}>
      {children}
    </TabContext.Provider>
  );
};

/**
 * Model type for CMS product models
 */
interface ProductModel {
  id?: string;
  modelId?: number;
  modelName?: string;
  slug?: string;
  tabs?: CmsNode[];
}

/**
 * Tab metadata type (lightweight - no content)
 */
interface TabMetadata {
  slug?: string;
  title?: string;
  _modelApiKey?: string;
}

/**
 * ProductTabsNavigation - Renders TabsNavigation with lazy-loaded tab content
 *
 * Two-Phase Architecture:
 * 1. Initial load: Only tab metadata (slug, title) - no content
 * 2. On tab selection: Fetch full content for selected tab via useTabContent hook
 *
 * Features:
 * - Default tab: "Overview" or first tab
 * - Slug-based identification (Tab model has no id field)
 * - Content caching per tab
 * - Model-based tabs support (e.g., Guided Selling)
 */
const ProductTabsNavigation: React.FC<{
  children?: React.ReactNode;
  tabs?: CmsNode[];
  models?: ProductModel[];
}> = ({ children, tabs = [], models = [] }) => {
  // Use Redux directly for synchronous access - eliminates race conditions
  const selectedModelId = useSelector(
    (state: {
      filtersV2?: {
        filters?: Record<string, { selectedValue?: number | string | null }>;
      };
    }) => selectFilterValue(state, 'model')
  );
  const productId = useSelector(selectCmsCurrentProductId);

  // Still need FilterContext for tab name syncing (FrequencyFilter) and betaFeatureToggle
  const { setCurrentTabName, setCurrentSubTabName, betaFeatureToggle } =
    useContext(FilterContext);

  // Get helper function for environment check
  const { isDevOrQaOrPreProdEnv } = helperFunctions();

  // Determine which tabs to show based on selected model from Redux
  const activeTabs = useMemo((): TabMetadata[] => {
    let tabsToShow: TabMetadata[] = [];

    // If direct tabs exist on product, use them (e.g., RGM APP)
    if (tabs.length > 0) {
      tabsToShow = tabs as TabMetadata[];
    } else if (models.length > 0 && selectedModelId != null) {
      // If models exist, find the matching model's tabs using Redux selectedModelId
      const selectedIdNum = Number(selectedModelId);
      const matchingModel = models.find(
        (m) => m.modelId === selectedIdNum || m.modelId === selectedModelId
      );
      tabsToShow = (matchingModel?.tabs || []) as TabMetadata[];
    }

    // For PepsiConnect, apply betaFeatureToggle filter
    // Only Overview and Success Report are shown without beta toggle (unless in dev/qa/preprod)
    if (
      productId === constants.products.PepsiConnect &&
      !betaFeatureToggle &&
      !isDevOrQaOrPreProdEnv()
    ) {
      const allowedWithoutBeta = new Set([
        'st-pepsiconnect-success-report',
        'st-pepsiconnect-overview-tab',
      ]);
      tabsToShow = tabsToShow.filter((tab) =>
        allowedWithoutBeta.has(tab.slug?.toLowerCase() || '')
      );
    }

    return tabsToShow;
  }, [tabs, models, selectedModelId, productId, betaFeatureToggle]);

  // Get model ID for content fetching (undefined for direct product tabs)
  const currentModelId = useMemo((): number | undefined => {
    // Direct tabs - no model
    if (tabs.length > 0) {
      return undefined;
    }

    // Model-based tabs - use Redux selectedModelId directly
    if (models.length > 0 && selectedModelId != null) {
      const selectedIdNum = Number(selectedModelId);
      const matchingModel = models.find(
        (m) => m.modelId === selectedIdNum || m.modelId === selectedModelId
      );
      return matchingModel?.modelId;
    }

    return undefined;
  }, [tabs, models, selectedModelId]);

  // Initialize with default tab slug (Overview or first tab)
  const [selectedTabSlug, setSelectedTabSlug] = useState<string | null>(() => {
    return getDefaultTabSlug(activeTabs as { slug?: string; title?: string }[]);
  });

  // Reset to default tab when model/tabs change and sync to FilterContext
  useEffect(() => {
    const defaultSlug = getDefaultTabSlug(
      activeTabs as { slug?: string; title?: string }[]
    );
    setSelectedTabSlug(defaultSlug);

    // Sync to FilterContext so FrequencyFilter picks up the active CMS tab
    const defaultTab = activeTabs.find((t) => t.slug === defaultSlug);
    if (defaultTab) {
      setCurrentTabName(defaultTab.title || defaultTab.slug || null);
      setCurrentSubTabName(null);
    }
  }, [activeTabs, setCurrentTabName, setCurrentSubTabName]);

  // Lazy load content for selected tab
  // Simplified: Redux provides synchronous access, so we just need valid params
  const isTabSlugValid =
    !!selectedTabSlug &&
    activeTabs.length > 0 &&
    activeTabs.some((t) => t.slug === selectedTabSlug);

  // For model-based products, require currentModelId; for direct tabs, it's undefined
  const shouldAutoFetch =
    isTabSlugValid && !!productId && (tabs.length > 0 || !!currentModelId);

  const { content, isLoading, error } = useTabContent({
    productId: productId || 0,
    modelId: currentModelId,
    tabSlug: selectedTabSlug || '',
    autoFetch: shouldAutoFetch,
  });

  // Find selected index for TabsNavigation
  const selectedIndex = useMemo(() => {
    return findTabIndexBySlug(
      activeTabs as { slug?: string }[],
      selectedTabSlug || ''
    );
  }, [activeTabs, selectedTabSlug]);

  // Handle tab change
  const handleTabChange = (newIndex: number): void => {
    const newTab = activeTabs[newIndex];
    if (newTab?.slug) {
      setSelectedTabSlug(newTab.slug);
      // Sync to FilterContext so FrequencyFilter reacts to tab change
      setCurrentTabName(newTab.title || newTab.slug);
      setCurrentSubTabName(null);
    }
  };

  // Check if we're still waiting for fetch conditions to be met
  const isWaitingForFetchConditions =
    !shouldAutoFetch && !content && !error && activeTabs.length > 0;

  // Render tab content
  const renderContent = (): React.ReactNode => {
    // Show loader if actively loading OR waiting for fetch conditions
    if (isLoading || isWaitingForFetchConditions) {
      return (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      );
    }

    if (error) {
      return <ErrorMessage>Failed to load tab content: {error}</ErrorMessage>;
    }
    if (content && Array.isArray(content) && content.length > 0) {
      return content.map((contentNode: CmsNode, index: number) => (
        <CmsRenderer
          key={contentNode.id || contentNode.slug || `content-${index}`}
          node={contentNode}
        />
      ));
    }

    // Fallback to children if no content
    if (children) {
      return children;
    }

    return <EmptyState>No content available for this tab</EmptyState>;
  };

  // Don't render if no tabs (model-based products will have empty tabs until model selected)
  if (activeTabs.length === 0) {
    return children ? <>{children}</> : null;
  }

  return (
    <StyledProductTabs>
      <FullWidthTabsContainer>
        <TabsNavigation
          selectedIndex={selectedIndex}
          onUpdate={handleTabChange}
          type="line"
          orientation="horizontal"
        >
          {activeTabs.map((tab, index) => (
            <Tab
              key={tab.slug || `tab-${index}`}
              label={tab.title || tab.slug || `Tab ${index + 1}`}
              index={index}
              isActive={selectedIndex === index}
              type="line"
            />
          ))}
        </TabsNavigation>
      </FullWidthTabsContainer>

      {/* Render the selected tab's content (lazy loaded) */}
      <StyledTabContent data-tab-content="true">
        {renderContent()}
      </StyledTabContent>
    </StyledProductTabs>
  );
};

export default ProductTabsNavigation;
